/**
 * Self-test suite for the call reconnection state machine and UI policy.
 *
 * Web port of `kaether_for_ios/Stoat/E2EE/CallReconnectionSelfTest.swift`.
 *
 * Sections:
 *   A. Basic state transitions (event-driven).
 *   B. Time-based deadline transitions (tick-driven).
 *   C. Recovery: reconnecting → connected.
 *   D. Critical invariant: `disconnectedAfterFailure` ONLY via tick.
 *   E. Terminal state absorption.
 *   F. UI policy — path-aware banner copy.
 *
 * All tests are pure (no I/O, no DOM, no async). The clock is injected as a
 * numeric parameter so tests can advance time deterministically.
 *
 * Registered with the global SelfTestRunner singleton at module load time.
 * Auto-discovered by Vite `import.meta.glob` in `selftest/index.ts`.
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import {
  makeConnecting,
  UNSTABLE_TO_RECONNECTING_MS,
  RECONNECTING_TO_FAILED_MS,
} from "./CallReconnectionState";
import { transition, tick } from "./CallReconnectionStateMachine";
import { reconnectionBannerPolicy } from "./CallReconnectionUIPolicy";

// ---------------------------------------------------------------------------
// Fixture helpers
// ---------------------------------------------------------------------------

const T0 = 1_000_000; // arbitrary epoch offset — keeps test timestamps legible

// ---------------------------------------------------------------------------
// Test cases
// ---------------------------------------------------------------------------

const cases: TestCase[] = [];

// ── A. Basic state transitions ──────────────────────────────────────────────

// A1: connecting → connected on `connected` event
{
  const initial = makeConnecting(T0);
  const next = transition(initial, { kind: "connected" });
  cases.push(
    next.kind === "connected"
      ? pass("A1: connecting → connected event → connected")
      : fail(
          "A1: connecting → connected event → connected",
          `Expected connected, got ${next.kind}`,
        ),
  );
}

// A2: connected → unstable on `networkDrop` event
{
  const connected = { kind: "connected" as const };
  const next = transition(connected, {
    kind: "networkDrop",
    networkPath: "wifi",
  });
  cases.push(
    next.kind === "unstable"
      ? pass("A2: connected → networkDrop event → unstable")
      : fail(
          "A2: connected → networkDrop event → unstable",
          `Expected unstable, got ${next.kind}`,
        ),
  );
}

// A3: unstable + networkPath is preserved
{
  const connected = { kind: "connected" as const };
  const next = transition(connected, {
    kind: "networkDrop",
    networkPath: "cellular",
  });
  cases.push(
    next.kind === "unstable" && next.networkPath === "cellular"
      ? pass("A3: networkDrop preserves networkPath=cellular in unstable state")
      : fail(
          "A3: networkDrop preserves networkPath=cellular in unstable state",
          `Got kind=${next.kind}, networkPath=${next.kind === "unstable" ? next.networkPath : "n/a"}`,
        ),
  );
}

// A4: callEnded from any state → ended
{
  const states = [
    makeConnecting(T0),
    { kind: "connected" as const },
    { kind: "unstable" as const, enteredAt: T0, networkPath: "wifi" as const },
    { kind: "reconnecting" as const, enteredAt: T0 },
  ] as const;
  let allEnded = true;
  let failDetail = "";
  for (const s of states) {
    const next = transition(s, { kind: "callEnded" });
    if (next.kind !== "ended") {
      allEnded = false;
      failDetail = `State ${s.kind} → callEnded produced ${next.kind}`;
      break;
    }
  }
  cases.push(
    allEnded
      ? pass("A4: callEnded from any non-terminal state → ended")
      : fail("A4: callEnded from any non-terminal state → ended", failDetail),
  );
}

// ── B. Tick-based deadline transitions ──────────────────────────────────────

// B1: unstable + tick within 3s → still unstable
{
  const unstable = {
    kind: "unstable" as const,
    enteredAt: T0,
    networkPath: "wifi" as const,
  };
  const within = tick(unstable, T0 + UNSTABLE_TO_RECONNECTING_MS - 1);
  cases.push(
    within.kind === "unstable"
      ? pass("B1: unstable + tick within 3s → still unstable")
      : fail(
          "B1: unstable + tick within 3s → still unstable",
          `Expected unstable, got ${within.kind}`,
        ),
  );
}

// B2: unstable + tick beyond 3s → reconnecting
{
  const unstable = {
    kind: "unstable" as const,
    enteredAt: T0,
    networkPath: "wifi" as const,
  };
  const beyond = tick(unstable, T0 + UNSTABLE_TO_RECONNECTING_MS + 1);
  cases.push(
    beyond.kind === "reconnecting"
      ? pass("B2: unstable + tick beyond 3s → reconnecting")
      : fail(
          "B2: unstable + tick beyond 3s → reconnecting",
          `Expected reconnecting, got ${beyond.kind}`,
        ),
  );
}

// B3: reconnecting + tick within 30s → still reconnecting
{
  const reconnecting = { kind: "reconnecting" as const, enteredAt: T0 };
  const within = tick(reconnecting, T0 + RECONNECTING_TO_FAILED_MS - 1);
  cases.push(
    within.kind === "reconnecting"
      ? pass("B3: reconnecting + tick within 30s → still reconnecting")
      : fail(
          "B3: reconnecting + tick within 30s → still reconnecting",
          `Expected reconnecting, got ${within.kind}`,
        ),
  );
}

// B4: reconnecting + tick beyond 30s → disconnectedAfterFailure
{
  const reconnecting = { kind: "reconnecting" as const, enteredAt: T0 };
  const beyond = tick(reconnecting, T0 + RECONNECTING_TO_FAILED_MS + 1);
  cases.push(
    beyond.kind === "disconnectedAfterFailure"
      ? pass("B4: reconnecting + tick beyond 30s → disconnectedAfterFailure")
      : fail(
          "B4: reconnecting + tick beyond 30s → disconnectedAfterFailure",
          `Expected disconnectedAfterFailure, got ${beyond.kind}`,
        ),
  );
}

// ── C. Recovery ──────────────────────────────────────────────────────────────

// C1: reconnecting + connected event → connected (recovered)
{
  const reconnecting = { kind: "reconnecting" as const, enteredAt: T0 };
  const recovered = transition(reconnecting, { kind: "connected" });
  cases.push(
    recovered.kind === "connected"
      ? pass("C1: reconnecting + connected event → connected (recovered)")
      : fail(
          "C1: reconnecting + connected event → connected (recovered)",
          `Expected connected, got ${recovered.kind}`,
        ),
  );
}

// C2: unstable + connected event → connected (recovered before deadline)
{
  const unstable = {
    kind: "unstable" as const,
    enteredAt: T0,
    networkPath: "wifi" as const,
  };
  const recovered = transition(unstable, { kind: "connected" });
  cases.push(
    recovered.kind === "connected"
      ? pass("C2: unstable + connected event → connected (recovered before deadline)")
      : fail(
          "C2: unstable + connected event → connected (recovered before deadline)",
          `Expected connected, got ${recovered.kind}`,
        ),
  );
}

// ── D. Critical invariant: disconnectedAfterFailure ONLY via tick ────────────

// D1: A single event in reconnecting state CANNOT produce disconnectedAfterFailure.
//     Test by injecting every known event type.
{
  const reconnecting = { kind: "reconnecting" as const, enteredAt: T0 };
  const forgedEvents = [
    { kind: "connected" as const },
    { kind: "networkDrop" as const, networkPath: "cellular" as const },
    { kind: "callEnded" as const },
  ] as const;

  let anyForgedTerminated = false;
  let forgedDetail = "";
  for (const event of forgedEvents) {
    const result = transition(reconnecting, event);
    if (result.kind === "disconnectedAfterFailure") {
      anyForgedTerminated = true;
      forgedDetail = `Event kind="${event.kind}" drove reconnecting → disconnectedAfterFailure (MUST NOT happen)`;
      break;
    }
  }
  cases.push(
    !anyForgedTerminated
      ? pass(
          "D1 [CRITICAL]: forged events in reconnecting state cannot produce disconnectedAfterFailure — only tick(now) with elapsed>30s can",
        )
      : fail(
          "D1 [CRITICAL]: forged events in reconnecting state cannot produce disconnectedAfterFailure — only tick(now) with elapsed>30s can",
          forgedDetail,
        ),
  );
}

// D2: Confirm tick DOES produce disconnectedAfterFailure (positive control).
{
  const reconnecting = { kind: "reconnecting" as const, enteredAt: T0 };
  const tickResult = tick(reconnecting, T0 + RECONNECTING_TO_FAILED_MS + 1);
  cases.push(
    tickResult.kind === "disconnectedAfterFailure"
      ? pass(
          "D2 [CRITICAL]: tick with elapsed>30s in reconnecting → disconnectedAfterFailure (positive control)",
        )
      : fail(
          "D2 [CRITICAL]: tick with elapsed>30s in reconnecting → disconnectedAfterFailure (positive control)",
          `Expected disconnectedAfterFailure, got ${tickResult.kind}`,
        ),
  );
}

// ── E. Terminal state absorption ──────────────────────────────────────────────

// E1: ended + any event → still ended
{
  const ended = { kind: "ended" as const };
  const events = [
    { kind: "connected" as const },
    { kind: "networkDrop" as const, networkPath: "wifi" as const },
    { kind: "callEnded" as const },
  ] as const;
  let allAbsorbed = true;
  let absorbDetail = "";
  for (const event of events) {
    const next = transition(ended, event);
    if (next.kind !== "ended") {
      allAbsorbed = false;
      absorbDetail = `ended + event "${event.kind}" → ${next.kind} (should stay ended)`;
      break;
    }
  }
  cases.push(
    allAbsorbed
      ? pass("E1: ended is terminal — all events absorbed")
      : fail("E1: ended is terminal — all events absorbed", absorbDetail),
  );
}

// E2: disconnectedAfterFailure + any event → still disconnectedAfterFailure
{
  const daf = { kind: "disconnectedAfterFailure" as const };
  const events = [
    { kind: "connected" as const },
    { kind: "networkDrop" as const, networkPath: "wifi" as const },
    { kind: "callEnded" as const },
  ] as const;
  let allAbsorbed = true;
  let absorbDetail = "";
  for (const event of events) {
    const next = transition(daf, event);
    if (next.kind !== "disconnectedAfterFailure") {
      allAbsorbed = false;
      absorbDetail = `disconnectedAfterFailure + event "${event.kind}" → ${next.kind}`;
      break;
    }
  }
  cases.push(
    allAbsorbed
      ? pass("E2: disconnectedAfterFailure is terminal — all events absorbed")
      : fail(
          "E2: disconnectedAfterFailure is terminal — all events absorbed",
          absorbDetail,
        ),
  );
}

// E3: tick on terminal states → unchanged
{
  const terminals = [
    { kind: "disconnectedAfterFailure" as const },
    { kind: "ended" as const },
  ] as const;
  let allUnchanged = true;
  let unchangedDetail = "";
  for (const s of terminals) {
    const next = tick(s, T0 + 99_999);
    if (next.kind !== s.kind) {
      allUnchanged = false;
      unchangedDetail = `tick on ${s.kind} → ${next.kind}`;
      break;
    }
  }
  cases.push(
    allUnchanged
      ? pass("E3: tick on terminal states → unchanged")
      : fail("E3: tick on terminal states → unchanged", unchangedDetail),
  );
}

// ── F. UI policy — path-aware banner copy ────────────────────────────────────

// F1: unstable + cellular path → cellular-specific copy
{
  const unstable = {
    kind: "unstable" as const,
    enteredAt: T0,
    networkPath: "cellular" as const,
  };
  const policy = reconnectionBannerPolicy(unstable);
  const isCellularCopy =
    policy.showBanner &&
    policy.severity === "warning" &&
    policy.title.toLowerCase().includes("mobile");
  cases.push(
    isCellularCopy
      ? pass("F1: unstable + cellular → cellular-specific banner copy")
      : fail(
          "F1: unstable + cellular → cellular-specific banner copy",
          `Got title="${policy.title}", showBanner=${policy.showBanner}, severity=${policy.severity}`,
        ),
  );
}

// F2: unstable + wifi path → Wi-Fi-specific copy
{
  const unstable = {
    kind: "unstable" as const,
    enteredAt: T0,
    networkPath: "wifi" as const,
  };
  const policy = reconnectionBannerPolicy(unstable);
  const isWifiCopy =
    policy.showBanner &&
    policy.severity === "warning" &&
    policy.title.toLowerCase().includes("wi-fi");
  cases.push(
    isWifiCopy
      ? pass("F2: unstable + wifi → Wi-Fi-specific banner copy")
      : fail(
          "F2: unstable + wifi → Wi-Fi-specific banner copy",
          `Got title="${policy.title}", showBanner=${policy.showBanner}, severity=${policy.severity}`,
        ),
  );
}

// F3: connected state → no banner shown
{
  const policy = reconnectionBannerPolicy({ kind: "connected" });
  cases.push(
    !policy.showBanner && policy.severity === "none"
      ? pass("F3: connected state → no banner (severity=none)")
      : fail(
          "F3: connected state → no banner (severity=none)",
          `Got showBanner=${policy.showBanner}, severity=${policy.severity}`,
        ),
  );
}

// F4: disconnectedAfterFailure → error-severity banner
{
  const policy = reconnectionBannerPolicy({
    kind: "disconnectedAfterFailure",
  });
  cases.push(
    policy.showBanner && policy.severity === "error"
      ? pass("F4: disconnectedAfterFailure → error-severity banner")
      : fail(
          "F4: disconnectedAfterFailure → error-severity banner",
          `Got showBanner=${policy.showBanner}, severity=${policy.severity}`,
        ),
  );
}

// F5: reconnecting → warning-severity banner
{
  const policy = reconnectionBannerPolicy({
    kind: "reconnecting",
    enteredAt: T0,
  });
  cases.push(
    policy.showBanner && policy.severity === "warning"
      ? pass("F5: reconnecting → warning-severity banner")
      : fail(
          "F5: reconnecting → warning-severity banner",
          `Got showBanner=${policy.showBanner}, severity=${policy.severity}`,
        ),
  );
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Call reconnection (network-change handling)",
  isVerified: true,
  run: async () => buildSuiteResult(cases),
});
